import { Component } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import Vue from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { router, validation } from '@/_helpers';

import MDTextFieldWithValidation from '@/components/Validation/MDTextFieldWithValidation';
import MDTextAreaWithValidation from '@/components/Validation/MDTextAreaWithValidation';
import ConfirmationPopup from '@/components/ConfirmationPopup';
import countries from '@/_helpers/countries';
import customer from '@/store/modules/customer';
import store from '@/store';
import { Customer, CustomerAttributesAdmin, CustomerDetailsEdit } from '@/store/models';
import filters from '@/_helpers/filters';
import { Subscription } from '@/store/models/subscriptions';

import notifier from '@/_helpers/notifier';
import customerAttributes from '@/store/modules/customerAttributes';
import copyToClipboard from '@/_helpers/copy';
import template from './Details.vue';

const usageStatIcons = {
	qrCodes: 'mdi-qrcode',
	qrLinks: 'mdi-link-variant',
	users: 'mdi-account-multiple-outline',
	scans: 'mdi-cellphone-android',
	storage: 'mdi-server'
};

dayjs.extend(utc, { parseToLocal: true });

@Component({
	components: {
		ValidationObserver,
		MDTextFieldWithValidation,
		MDTextAreaWithValidation,
		ConfirmationPopup
	},
	mixins: [template],
	methods: {
		formatBytes: filters.formatBytes,
		formatUsers: filters.formatUsers,
		formatScans: filters.formatScans
	}
})
export default class customerDetailsEdit extends Vue {
	loading = true;

	stepper: number = 0;

	countries: {}[] = [];

	show: boolean = false;

	deleteId: string = '';

	usageStats: object[] = [
		{
			name: 'qrCodes'
		},
		{
			name: 'qrLinks'
		},
		{
			name: 'users'
		},
		{
			name: 'scans'
		},
		{
			name: 'storage'
		}
	];

	get darkMode(): boolean {
		return store.state.darkMode;
	}

	subscriptions: Subscription[] = [];

	progress: any = null;

	endDateModal: boolean = false;

	endTimeModal: boolean = false;

	trialEndDate: string = '';

	trialEndTime: string = '';

	async mounted(): Promise<void> {
		this.countries = countries;
		this.loading = true;

		try {
			await customer.get(this.$route.params.Cid);
			this.subscriptions = ((await customer.getSubscription(this.$route.params.Cid)) as unknown) as Subscription[];

			if (this.subscriptions.length === 0) {
				this.progress = (await customer.getProgressCustomerID(this.$route.params.Cid)).data;

				const end = dayjs(this.progress.trialEndAt);
				this.trialEndDate = end.format('YYYY-MM-DD');
				this.trialEndTime = end.format('HH:mm');
			}

			await customerAttributes.get(this.$route.params.Cid);
		} catch (error) {
			notifier.error('getting', 'user');
		} finally {
			this.loading = false;
		}
	}

	endDatePickerFocus() {
		this.endDateModal = true;
	}

	formatSubscriptionDate(date: string): string {
		if (!date) {
			return '';
		}
		return dayjs(date).format('DD MMMM, YYYY | HH:mm');
	}

	formatDate(date: string): string {
		if (!date) {
			return '';
		}
		return dayjs(date).format('DD MMMM, YYYY');
	}

	async save() {
		const isValid = await (this.$refs.form as any).validate();
		if (!isValid) {
			return;
		}

		this.loading = true;

		this.storeCustomer();

		if (this.subscriptions.length === 0) {
			this.storeProgress();
		}

		this.storeAttributes();

		this.loading = false;
	}

	async storeCustomer(): Promise<void> {
		const params: CustomerDetailsEdit = {
			name: this.customer.name,
			email: this.customer.email,
			phoneNumber: this.customer.phoneNumber,
			invoiceSettings: {
				footer: this.customer.invoiceSettings.footer
			},
			id: this.$route.params.Cid,
			type: this.customer.type,
			vat: this.customer.vat
		};

		await customer
			.put(params)
			.then((): void => {
				notifier.success('updated', 'user');
				this.loading = false;
			})
			.catch((e): void => {
				(this.$refs.form as any).setErrors(validation.getErrors(e.errors));
				notifier.error('updating', 'user');
				this.loading = false;
			});
	}

	async storeProgress(): Promise<void> {
		const end = dayjs(`${this.trialEndDate} ${this.trialEndTime}`).utc();
		this.progress.trialEndAt = end.format('YYYY-MM-DDTHH:mm:ss[Z]');

		try {
			await customer.putProgress({ customerID: this.$route.params.Cid, progress: this.progress });
			notifier.success('updated', 'progress');
		} catch {
			notifier.error('updating', 'progress');
		}
	}

	async storeAttributes(): Promise<void> {
		const params: CustomerAttributesAdmin = {
			id: this.attributesAdmin.id,
			customerId: this.attributesAdmin.customerId,
			webscan: {
				showQrLinkLogo: this.attributesAdmin.webscan.showQrLinkLogo
			},
			other: {}
		};

		try {
			await customerAttributes.put(params);
		} catch {
			notifier.error('updating', 'progress');
		} finally {
			this.loading = false;
		}
	}

	get customer(): Customer {
		return customer.customer;
	}

	get attributesAdmin(): CustomerAttributesAdmin {
		return customerAttributes.attributesAdmin;
	}

	popup(id: string): void {
		this.show = true;
		this.deleteId = id;
	}

	cancel(): void {
		this.show = false;
		this.deleteId = '';
	}

	confirm(): void {
		this.show = false;

		// eslint-disable-next-line radix
		const id: number = parseInt(this.deleteId);

		if (!id) {
			notifier.error('deleting', 'customer');
			return;
		}

		this.loading = true;

		customer
			.destroy(`/customers/${id}`)
			.then((): void => {
				this.loading = false;
				notifier.success('deleted', 'customer');
				router.go(-1);
			})
			.catch((e): void => {
				this.loading = false;
				notifier.error('deleting', 'customer');
			});
	}

	getUsageStatIcon(usageStatName: string): string {
		// @ts-ignore
		return usageStatIcons[usageStatName] || '';
	}

	copy(text: string): void {
		copyToClipboard(text, 'link');
	}
}
